import { HttpDataService, IHttpConfig } from '../../http/http-data.service';
import { ICodeValue, ICompanyCountry, ICountryExtended, IDescribedObject, IGenericUser, ILanguage, ISubCompany, IUser } from '../../../../type_definitions/datatypes';
import { Injectable } from '@angular/core';

export interface IAuthenticateResponse {
  status:string;
  lifetime:number;
  token:string;
  alternateUserId?:number;
  message?:string;
}

export interface IAccessTypes {
  // security domain -> securityRole
  [ key:string ]:ISecurityRole;
}

export interface ISecurityRole {
  // security object -> 'R' or 'W' or 'S'
  [ key:string ]:Permission;
}

export type Permission = 'R' | 'W' | 'S';

export interface IUserWebshop {
  remainingSpendLimit: number;
  spendLimitCurrency: string;
  totalSpendLimit: number;
  webshopName: string;
}

export interface IUserData extends IUser {
  language:ILanguage;
  userType:string;
  skinCode:string;
  gaLayout:IDescribedObject;
  genericUser:IGenericUser;
  accessTypes:IAccessTypes;
  companyDepartment?:ICompanyCountry;
  directPhone:string;
  officePhone:string;
  cellPhone:string;
  faxNumber:string;
  hasProfilePicture:boolean;
  office:string;
  registrationState:string;
  token?:string; // used when a new user sets the password
  userRegistrationApproved:boolean;
  signupCompany?:string;
  signupSubCompany?:string;
  signupCompanyType?:string;
  signupRole?:string;
  signupCountry?:ICountryExtended;
  useGA2Beta:boolean;
  preferredCommunication?:string;
  privacyAgreed:boolean;
  comment:string;
  localCredentialsAlwaysAllowed:boolean;
  ssoLogoutUrl:string;
  subCompany:ISubCompany;
  noMenu:boolean;
  companyEditable:boolean;
  autoApproval:boolean;
  userTraining?:{
    lastTrainingDate:number;
    trainingStatus:string;
    acslComment:string;
    responsive?:boolean;
    usingGA?:boolean;
    canDemoGA?:boolean;
  };
  weChatId:string;
  whatsappId:string;
  userCategory:string;
  linkedin?:string;
  fluentEnglish?:boolean;
  updateMethod?:ICodeValue;
  updateComments?:string;
  updateFrequency?:string;
  timeSlotForGAUpdates?:string;
  checkpoint?:string;
  userWebshops: IUserWebshop[];
  timezone?:string;
}

@Injectable()
export class AuthDataService {

  public static CREATE_SESSION_URL:string = '/unacc/CreateSession';
  public static DESTROY_SESSION_URL:string = '/unacc/DestroySession';

  public constructor(
    private httpDataService:HttpDataService
  ) {
    /**/
  }

  // posts the username, password, asUser to /authenticate and returns the AuthenticationResponses if the status is OK,
  // or returns the failure message if there is one
  public authenticate(username:string, password:string, asUser?:string):Promise<IAuthenticateResponse> {

    const config:IHttpConfig = {
      url: 'authenticate',
      method: 'POST',
      options: {
        body: $.param(asUser? {
          username,
          password,
          asUser
        } :
        {
          username,
          password
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
    };

    return this.httpDataService.doHTTP(config)
      .then((response:IAuthenticateResponse) => {
        if (response.status === 'OK') {
          return response;
        } else {
          return Promise.reject(response.status);
        }
      });
  }

  public loadCurrentUser():Promise<IUserData> {
    return this.httpDataService.doGET('rest/userman/currentuser');
  }

  public createSession(punchoutMessageId:string, theme:string):Promise<any> {
    const params:any = {};
    if (punchoutMessageId) {
      params.punchoutMessageId = punchoutMessageId;
    }
    if (theme) {
      params.theme = theme;
    }

    return this.httpDataService.doGET(AuthDataService.CREATE_SESSION_URL, params);
  }

  public destroySession():Promise<any> {
    const config:IHttpConfig = {
      url: AuthDataService.DESTROY_SESSION_URL,
      method: 'GET',
      options: {
        responseType: <any>'text'
      }
    };
    return this.httpDataService.doHTTP(config);
  }
}